import { useState, useCallback, useMemo, useEffect } from 'react'
import type { QuizSolveResult, BaseQuizData } from '../../../types/legacyQuiz'

export interface ImageSelectRect {
  key: string
  x: number
  y: number
  width: number
  height: number
  mobile?: {
    x: number
    y: number
    width: number
    height: number
  }
}

export interface ImageSelectQuizData extends BaseQuizData {
  type: 'image_select'
  choices: ImageSelectRect[]
}

export interface UseImageSelectQuizOptions {
  quizId: string | number
  quiz: ImageSelectQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export function useImageSelectQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseImageSelectQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers

  const computedInitialKeys = useMemo(() => {
    if (initialAnswersProp !== undefined) {
      return new Set(initialAnswersProp)
    }
    if (isViewSolved && solveResult?.correctAnswers) {
      return new Set(solveResult.correctAnswers)
    }
    return new Set<string>()
  }, [isViewSolved, solveResult, initialAnswersProp])

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(computedInitialKeys)

  useEffect(() => {
    setSelectedKeys(computedInitialKeys)
  }, [computedInitialKeys])

  useEffect(() => {
    onAnswerChange?.(Array.from(selectedKeys))
  }, [selectedKeys, onAnswerChange])

  const canSubmit = selectedKeys.size > 0

  const handleToggle = useCallback((key: string) => {
    if (isSolved || isViewSolved) return
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [isSolved, isViewSolved])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    const answersArray = Array.from(selectedKeys)
    onSolve?.(quizId, answersArray, quiz.vars, elapsedTime)
  }, [selectedKeys, canSubmit, isSolved, startTime, onSolve, quizId, quiz.vars])

  return {
    selectedKeys,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggle,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
